/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React from 'react'
import clsx from 'clsx'
import { createPortal } from 'react-dom'
import { ZinButton, ZinIconButton } from '@tnl/zin-react'
import { useScrollLock } from '@hooks/useScrollLock'
import './Dialog.scss'
import { useSiteContext } from '@shared/contexts/MkSiteContext'

export interface DialogProps {
  // Core modal props
  isOpen?: boolean
  visible?: boolean
  onClose?: () => void
  children: React.ReactNode
  className?: string

  // Dialog-specific props
  appendTo?: HTMLElement
  modal?: boolean

  // Content props
  title?: string
  headerAction?: {
    label: string
    onClick: () => void
    icon?: string
  }

  // Close button position
  closeButtonPosition?: 'header' | 'floating' | 'background' | 'content'

  // Background media props
  image?: {
    src: string
    alt: string
  }
  video?: {
    src: string
    type?: string
  }

  // Legacy prop support
  onHide?: () => void
}

export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  visible,
  onClose,
  onHide,
  children,
  className,
  appendTo,
  modal = true,
  title,
  headerAction,
  closeButtonPosition = 'content',
  image,
  video,
}) => {
  // Determine visibility state
  const isVisible = isOpen ?? visible ?? false
  const dialogRef = React.useRef<HTMLDialogElement>(null)
  const [dialogHeight, setDialogHeight] = React.useState<number>(0)
  const { themeBrand } = useSiteContext()

  const [isClosing, setIsClosing] = React.useState(false)

  if (themeBrand === 'ebac') {
    // eslint-disable-next-line global-require
    require(`./Dialog_ebac.scss`)
  }

  // Lock scroll when dialog is open
  useScrollLock(isVisible)

  const handleClose = React.useMemo(() => onClose ?? onHide, [onClose, onHide])

  const handlers = React.useMemo(
    () => ({
      keyDown: (e: React.KeyboardEvent<HTMLDialogElement>) => {
        if (e.key === 'Escape' && handleClose) {
          handleClose()
        }
      },
      headerAction: () => {
        if (headerAction?.onClick) {
          headerAction.onClick()
        }
      },
    }),
    [handleClose, headerAction?.onClick],
  )

  const classes = React.useMemo(
    () => ({
      dialog: clsx('dialog relative', isVisible && !isClosing && 'opacity-100 visible', 'data-[open]:opacity-100 data-[open]:visible', className),
      content: clsx('dialog__content'),
      header: clsx('dialog__header'),
      headerAction: clsx('dialog__header-action', 'flex flex-0 flex-1-auto justify-start'),
      title: 'dialog__title',
      close: 'dialog__close',
      floatingClose: clsx('dialog__floating-close', 'absolute top-4 right-4'),
      fullscreenModal: clsx('fullscreen-modal', { hidden: !isVisible }, 'flex items-start justify-center overflow-hidden', className),
      backgroundVideo: clsx('fullscreen-modal__background-video', 'absolute inset-0 overflow-hidden'),
      video: clsx('fullscreen-modal__video', 'h-full w-full object-cover object-center'),
      backgroundImage: clsx('fullscreen-modal__background', 'absolute inset-0 bg-cover bg-center bg-no-repeat'),
    }),
    [isVisible, isClosing, className],
  )

  const backgroundImageStyle = React.useMemo(() => (image ? { backgroundImage: `url(${image.src})` } : undefined), [image?.src])

  // Function to update dialog height
  const updateDialogHeight = React.useCallback(() => {
    const dialog = dialogRef.current
    if (dialog) {
      const height = dialog.offsetHeight
      setDialogHeight(height)
    }
  }, [])

  // Handle dialog lifecycle
  React.useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isVisible) {
      setIsClosing(false)

      if (modal) {
        dialog.showModal()
      } else {
        dialog.show()
      }

      // Update height after dialog is shown
      const updateHeight = (): void => {
        updateDialogHeight()
      }

      // Use requestAnimationFrame to ensure dialog is fully rendered
      requestAnimationFrame(updateHeight)

      // Also update on window resize
      const handleResize = (): void => {
        updateDialogHeight()
      }
      window.addEventListener('resize', handleResize)

      // eslint-disable-next-line consistent-return
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }

    setIsClosing(true)
    const closeTimeout = setTimeout(() => {
      dialog.close()
      setIsClosing(false)
    }, 300)
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(closeTimeout)
  }, [isVisible, modal, updateDialogHeight])

  // Memoize close button component
  const closeButton = React.useMemo(
    () => (
      <ZinIconButton
        title="Close"
        className="dialog__close-button"
        icon="xmark"
        onClick={handleClose}
        set="far"
        size="lg"
      />
    ),
    [handleClose],
  )

  // Memoize header content
  const headerContent = React.useMemo(() => {
    // Only render header if there's a title or headerAction
    if (!title && !headerAction) {
      return null
    }

    return (
      <div className={classes.header}>
        <div className={classes.headerAction}>
          {headerAction && (
            <>
              <ZinIconButton
                aria-label={headerAction.label}
                className={clsx('dialog__header-action--mobile', 'text-primary md:hidden')}
                icon={headerAction.icon || 'calendar'}
                onClick={handlers.headerAction}
                set="far"
              />
              <ZinButton
                className={clsx('dialog__header-action--desktop', 'text-primary hidden md:flex')}
                icon={headerAction.icon || 'calendar'}
                onClick={handlers.headerAction}>
                {headerAction.label}
              </ZinButton>
            </>
          )}
        </div>
        <div className={classes.title}>{title && <h2 className="uppercase text-sm font-medium">{title}</h2>}</div>
      </div>
    )
  }, [classes, headerAction, handlers.headerAction, title])

  // Memoize background media
  const backgroundMedia = React.useMemo(() => {
    if (!image && !video) return null

    return (
      <>
        {video && (
          <div className={clsx(classes.backgroundVideo, 'hidden md:block')}>
            <video
              autoPlay
              muted
              loop
              playsInline
              aria-hidden
              className={classes.video}>
              <source
                src={video.src}
                type={video.type || 'video/mp4'}
              />
              Your browser does not support the video tag.
            </video>
          </div>
        )}
        {image && !video && (
          <div
            className={clsx(classes.backgroundImage, 'hidden md:block')}
            style={backgroundImageStyle}
            role="img"
            aria-label={image.alt}
          />
        )}
      </>
    )
  }, [image, video, classes, backgroundImageStyle])

  // Memoize dialog content
  const dialogContent = React.useMemo(
    () => (
      <>
        {closeButtonPosition === 'background' && <div className={classes.floatingClose}>{closeButton}</div>}
        <dialog
          ref={dialogRef}
          className={classes.dialog}
          tabIndex={-1}
          onKeyDown={handlers.keyDown}
          aria-modal={modal}>
          <div className={classes.content}>
            {headerContent}
            {children}
            {closeButtonPosition === 'content' && <div className={classes.floatingClose}>{closeButton}</div>}
          </div>
        </dialog>
      </>
    ),
    [classes.dialog, handlers, modal, headerContent, children, classes.floatingClose, closeButton, closeButtonPosition, dialogHeight],
  )

  // Memoize final content
  const finalContent = React.useMemo(() => {
    const content =
      image || video ? (
        <div className={classes.fullscreenModal}>
          {backgroundMedia}
          {/* Background surface offset to bottom right by 1rem */}
          {dialogContent}
        </div>
      ) : (
        <div className={classes.fullscreenModal}>
          <div className="w-full h-full flex items-start justify-center overflow-hidden">{dialogContent}</div>
        </div>
      )

    return appendTo ? createPortal(content, appendTo) : content
  }, [image, video, classes.fullscreenModal, backgroundMedia, dialogContent, appendTo, closeButtonPosition, classes.floatingClose, closeButton])

  return finalContent
}
